<template>
  <div class="container-fluid mt-4" dir="rtl">
    
    <!-- 1. الفلاتر والتحكم -->
    <div class="card shadow-sm mb-4 border-top-danger no-print">
      <div class="card-header bg-white py-3 d-flex justify-content-between align-items-center">
        <h5 class="mb-0 fw-bold text-danger">
          <i class="fas fa-microscope ms-2"></i> فحص الشواذ والأخطاء (Anomalies Check)
        </h5>
        
        <!-- زر التعديل -->
        <button v-if="!isEditMode" @click="enableEditing" class="btn btn-outline-danger fw-bold btn-sm" :disabled="students.length === 0">
          <i class="fas fa-lock ms-2"></i> فتح التعديل
        </button>
        <button v-else @click="isEditMode = false" class="btn btn-danger text-white fw-bold btn-sm">
          <i class="fas fa-lock-open ms-2"></i> إغلاق التعديل
        </button>
      </div>

      <div class="card-body">
        <div class="row g-3 align-items-end">
          <div class="col-md-3">
            <label class="form-label fw-bold">الفصل الدراسي</label>
            <select v-model="form.term" class="form-select">
              <option v-for="t in terms" :key="t.id" :value="t">{{ t.name }}</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label fw-bold">المجموعة</label>
            <select v-model="form.group" class="form-select">
              <option v-for="g in groups" :key="g.id" :value="g.id">{{ g.group_name }}</option>
            </select>
          </div>
          <div class="col-md-3">
            <button @click="fetchData" class="btn btn-danger w-100 fw-bold" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm ms-2"></span>
              فحص الأخطاء
            </button>
          </div>
          <div class="col-md-3">
             <button @click="printPage" class="btn btn-dark w-100" :disabled="students.length === 0">
               <i class="fas fa-print ms-2"></i> طباعة التقرير
             </button>
          </div>
        </div>
      </div>
    </div>

    <!-- رسالة في حالة عدم وجود أخطاء -->
    <div v-if="searched && students.length === 0" class="alert alert-success text-center shadow-sm">
        <i class="fas fa-check-circle fa-2x mb-2 d-block"></i>
        <h5>ممتاز! لا توجد درجات شاذة أو أخطاء في هذه المجموعة.</h5>
    </div>

    <!-- 2. جدول العرض -->
    <div id="printMe" v-if="students.length > 0">
      
      <div class="text-center mb-3 d-none d-print-block">
          <h3 class="fw-bold text-decoration-underline">تقرير الدرجات الشاذة والمراجعة</h3>
          <h5>مجموعة رقم: {{ form.group }}</h5>
      </div>

      <div class="table-scroll-container shadow-sm border rounded">
        <table class="table table-bordered text-center align-middle mb-0">
          
          <thead class="table-dark sticky-header">
            <tr>
              <!-- <th width="50px">م</th> -->
              <th width="80px">سري</th>
              <!-- <th width="180px">اسم الطالب</th> -->
              <!-- رؤوس المواد -->
              <th v-for="sub in subjects" :key="sub.id" class="small-th">
                  {{ sub.name }} <br> <span class="text-warning small">({{ sub.max }})</span>
              </th>
              <th width="250px" class="bg-danger text-white">سبب التنبيه (المشكلة)</th>
            </tr>
          </thead>
          
          <tbody>
            <tr v-for="(student, index) in students" :key="student.id" class="row-anomaly">
              
              <!-- <td>{{ index + 1 }}</td> -->
              <td class="fw-bold font-monospace">{{ student.SecretKey }}</td>
              <!-- <td class="text-start pe-2 fw-bold small text-nowrap">{{ student.Arabic_Name }}</td> -->

              <!-- الدرجات (قابلة للتعديل) -->
              <td 
                v-for="sub in subjects" 
                :key="sub.id" 
                class="p-1 position-relative"
                :class="getCellClass(student.grades_list[sub.code], sub.main, sub.max)"
              >
                  <span v-if="!isEditMode" class="d-block py-2">
                     {{ formatGrade(student.grades_list[sub.code]) }}
                  </span>

                  <input 
                    v-else
                    type="number" 
                    class="form-control text-center fw-bold p-1 border-0 shadow-none"
                    style="background: transparent;"
                    v-model.number="student.grades_list[sub.code]"
                    @blur="saveGrade(student, sub.code, student.grades_list[sub.code])"
                  >
              </td>

              <!-- سبب المشكلة -->
              <td class="text-start px-2 small bg-light">
                  <ul class="mb-0 ps-3 text-danger fw-bold">
                      <li v-for="(issue, i) in student.audit_issues" :key="i">{{ issue }}</li>
                  </ul>
              </td>

            </tr>
          </tbody>
        </table>
      </div>
    </div>

  </div>
</template>

<script>
import api from '@/api/axios';
import Swal from 'sweetalert2';

export default {
  name: "AnomaliesReview",
  data() {
    return {
      loading: false,
      searched: false,
      isEditMode: false,
      terms: [], groups: [],
      students: [], subjects: [],
      form: { term: '', group: '' }
    };
  },
  mounted() {
    this.fetchInitData();
  },
  methods: {
    async fetchInitData() {
      try {
        const [t, g] = await Promise.all([api.get("/getterms"), api.get("/getgroups")]);
        this.terms = t.data; this.groups = g.data;
      } catch (e) {}
    },

    async fetchData() {
      if(!this.form.term || !this.form.group) return alert("اختر البيانات");
      
      this.loading = true;
      this.searched = true;
      this.isEditMode = false;
      try {
        const res = await api.get('/admin/anomalies', { params: this.form });
        this.students = res.data.students;
        this.subjects = res.data.subjects;
      } catch (e) { alert("خطأ"); } 
      finally { this.loading = false; }
    },

    // --- دوال التعديل (نفس الصفحة السابقة) ---
    async enableEditing() {
      const { value: password } = await Swal.fire({
        title: 'مطلوب إذن الأدمن',
        input: 'password',
        inputPlaceholder: 'كلمة المرور للتصحيح',
        showCancelButton: true,
      });

      if (password === '123456') { // استبدله بالتحقق الحقيقي
         this.isEditMode = true;
      }
    },

    async saveGrade(student, colName, value) {
        if(!this.isEditMode) return;
        try {
             await api.post('/save-single-degree', {
                id: student.id,
                col_name: colName,
                degree: value
            });
        } catch(e) {
            alert("فشل الحفظ");
        }
    },

    // --- التنسيقات ---
    getCellClass(degree, min, max) {
        if (degree > max) return 'bg-danger text-white fw-bold animate-pulse'; // خطأ فادح
        if (degree == 0) return 'bg-warning text-dark fw-bold'; // صفر
        if (degree == -1) return 'bg-secondary text-white'; 
        if (degree < min && degree >= 0) return 'text-danger fw-bold'; 
        if (degree == max) return 'text-success fw-bold border-2 border-success'; 
        return '';
    },

    formatGrade(val) {
        if(val == -1) return 'غ';
        return val;
    },

    printPage() { this.$htmlToPaper("printMe"); }
  }
};
</script>

<style scoped>
.border-top-danger { border-top: 4px solid #dc3545; }
.table-scroll-container { max-height: 75vh; overflow-y: auto; position: relative; }
.sticky-header th { position: sticky; top: 0; z-index: 10; background-color: #212529; color: white; }

/* تمييز صفوف الأخطاء */
.row-anomaly td { background-color: #fff5f5; }
.row-anomaly:hover td { background-color: #ffe0e0; }

@keyframes pulse {
  0% { opacity: 1; } 50% { opacity: 0.5; } 100% { opacity: 1; }
}
.animate-pulse { animation: pulse 1s infinite; }

@media print {
    .no-print { display: none !important; }
    .d-print-block { display: block !important; }
    .table-scroll-container { max-height: none; overflow: visible; border: none; }
    .sticky-header th { position: static; }
    
    /* إجبار الألوان */
    .bg-danger { background-color: #dc3545 !important; color: white !important; -webkit-print-color-adjust: exact; }
    .bg-warning { background-color: #ffc107 !important; -webkit-print-color-adjust: exact; }
    .row-anomaly td { background-color: #fff5f5 !important; -webkit-print-color-adjust: exact; }
}
</style>